import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, GripVertical, Trash2, LayoutList, Kanban, ChevronLeft,
  Type, Hash, Calendar, ToggleLeft, List, Link, Users, Clock,
  CheckSquare, Search, ChevronDown,
} from 'lucide-react';

const FIELD_TYPE_ICONS = {
  text: Type,
  number: Hash,
  date: Calendar,
  boolean: ToggleLeft,
  select: List,
  multi_select: CheckSquare,
  url: Link,
  object_relationship: Users,
  user: Users,
};

const SYSTEM_FIELDS = [
  { system_field: 'created_at', label: 'Created', icon: Clock },
  { system_field: 'updated_at', label: 'Updated', icon: Clock },
];

/**
 * Modal for creating/editing ObjectViews.
 *
 * Create mode: Two-step flow — 1) pick name + type, 2) configure columns/filters.
 * Edit mode: Goes directly to the configure step.
 */
function ViewEditorModal({
  isOpen,
  onClose,
  onSave,
  mode = 'create',
  initialView = null,
  availableFields = [],
  filterConfig = [],
}) {
  const [step, setStep] = useState(1);
  const [viewName, setViewName] = useState('');
  const [viewType, setViewType] = useState('table');
  const [groupByFieldId, setGroupByFieldId] = useState('');
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [dragIndex, setDragIndex] = useState(null);
  const [columnSearch, setColumnSearch] = useState('');
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && initialView) {
      setStep(2);
      setViewName(initialView.name || '');
      setViewType(initialView.view_type || 'table');
      setGroupByFieldId(initialView.group_by_field_id || '');
      setColumns(
        (initialView.columns || []).map((c, i) => ({
          field_definition_id: c.field_definition_id || null,
          system_field: c.system_field || null,
          label: c.label,
          sort_order: i,
          width: c.width,
        }))
      );
      setFilters(initialView.filters || {});
    } else {
      setStep(1);
      setViewName('');
      setViewType('table');
      setGroupByFieldId('');
      setColumns([]);
      setFilters({});
    }
    setColumnSearch('');
    setShowColumnPicker(false);
  }, [isOpen, mode, initialView]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  // Column management
  const toggleFieldColumn = (field) => {
    const exists = columns.some((c) => c.field_definition_id === field.id);
    if (exists) {
      setColumns((prev) => prev.filter((c) => c.field_definition_id !== field.id));
    } else {
      setColumns((prev) => [
        ...prev,
        {
          field_definition_id: field.id,
          system_field: null,
          label: field.name,
          sort_order: prev.length,
          width: null,
        },
      ]);
    }
  };

  const toggleSystemColumn = (sf) => {
    const exists = columns.some((c) => c.system_field === sf.system_field);
    if (exists) {
      setColumns((prev) => prev.filter((c) => c.system_field !== sf.system_field));
    } else {
      setColumns((prev) => [
        ...prev,
        {
          field_definition_id: null,
          system_field: sf.system_field,
          label: sf.label,
          sort_order: prev.length,
          width: null,
        },
      ]);
    }
  };

  const removeColumn = (index) => {
    setColumns((prev) => prev.filter((_, i) => i !== index));
  };

  const updateColumnLabel = (index, label) => {
    setColumns((prev) => prev.map((c, i) => (i === index ? { ...c, label } : c)));
  };

  // Drag reorder
  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = useCallback(
    (e, overIndex) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === overIndex) return;
      setColumns((prev) => {
        const next = [...prev];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(overIndex, 0, moved);
        return next;
      });
      setDragIndex(overIndex);
    },
    [dragIndex]
  );
  const handleDragEnd = () => setDragIndex(null);

  const handleSave = () => {
    if (!viewName.trim()) return;
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== undefined && v !== '')
    );
    onSave({
      name: viewName,
      filters: cleanFilters,
      columns: columns.map((c, i) => ({
        field_definition_id: c.field_definition_id,
        system_field: c.system_field,
        label: c.label,
        sort_order: i,
        width: c.width,
      })),
      view_type: viewType,
      group_by_field_id: viewType === 'kanban' ? groupByFieldId || null : null,
    });
  };

  const handleNext = () => {
    if (!viewName.trim()) return;
    setStep(2);
  };

  // Derived
  const selectFields = useMemo(
    () => availableFields.filter((f) => f.field_type === 'select'),
    [availableFields]
  );
  const filteredAvailableFields = useMemo(() => {
    if (!columnSearch.trim()) return availableFields;
    const q = columnSearch.toLowerCase();
    return availableFields.filter((f) => f.name.toLowerCase().includes(q));
  }, [availableFields, columnSearch]);
  const filteredSystemFields = useMemo(() => {
    if (!columnSearch.trim()) return SYSTEM_FIELDS;
    const q = columnSearch.toLowerCase();
    return SYSTEM_FIELDS.filter((sf) => sf.label.toLowerCase().includes(q));
  }, [columnSearch]);

  const canSave =
    viewName.trim() && (viewType !== 'kanban' || groupByFieldId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="w-full max-w-2xl mx-4 bg-white rounded-xl shadow-lg border border-gray-100 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {step === 2 && mode === 'create' && (
              <button
                onClick={() => setStep(1)}
                className="p-1 -ml-1 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <h3 className="text-base font-semibold text-gray-900">
              {mode === 'edit' ? 'Edit View' : step === 1 ? 'New View' : 'Configure View'}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {step === 1 ? (
            <StepOne
              viewName={viewName}
              setViewName={setViewName}
              viewType={viewType}
              setViewType={setViewType}
              onNext={handleNext}
            />
          ) : (
            <StepTwo
              viewType={viewType}
              viewName={viewName}
              setViewName={setViewName}
              mode={mode}
              groupByFieldId={groupByFieldId}
              setGroupByFieldId={setGroupByFieldId}
              selectFields={selectFields}
              columns={columns}
              dragIndex={dragIndex}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleDragEnd={handleDragEnd}
              updateColumnLabel={updateColumnLabel}
              removeColumn={removeColumn}
              availableFields={availableFields}
              filteredAvailableFields={filteredAvailableFields}
              filteredSystemFields={filteredSystemFields}
              columnSearch={columnSearch}
              setColumnSearch={setColumnSearch}
              showColumnPicker={showColumnPicker}
              setShowColumnPicker={setShowColumnPicker}
              toggleFieldColumn={toggleFieldColumn}
              toggleSystemColumn={toggleSystemColumn}
              filterConfig={filterConfig}
              filters={filters}
              handleFilterChange={handleFilterChange}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          {step === 1 ? (
            <button
              onClick={handleNext}
              disabled={!viewName.trim()}
              className="px-5 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="px-5 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {mode === 'edit' ? 'Update View' : 'Create View'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Step 1: Name + Type Selection ─── */

function StepOne({ viewName, setViewName, viewType, setViewType, onNext }) {
  return (
    <>
      {/* View Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">View Name</label>
        <input
          type="text"
          value={viewName}
          onChange={(e) => setViewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && viewName.trim() && onNext()}
          placeholder="e.g., Active Clients"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400"
          autoFocus
        />
      </div>

      {/* View Type Cards */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">View Type</label>
        <div className="grid grid-cols-2 gap-3">
          <ViewTypeCard
            type="table"
            selected={viewType === 'table'}
            onClick={() => setViewType('table')}
            icon={<LayoutList className="w-6 h-6" />}
            title="Table"
            description="Rows and columns — best for browsing and sorting data"
            preview={<TablePreview />}
          />
          <ViewTypeCard
            type="kanban"
            selected={viewType === 'kanban'}
            onClick={() => setViewType('kanban')}
            icon={<Kanban className="w-6 h-6" />}
            title="Kanban"
            description="Cards grouped by status — best for tracking progress"
            preview={<KanbanPreview />}
          />
        </div>
      </div>
    </>
  );
}

function ViewTypeCard({ selected, onClick, title, description, preview }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative text-left p-4 rounded-xl border-2 transition-all ${
        selected
          ? 'border-brand-500 bg-brand-50/50 ring-1 ring-brand-500/20'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50'
      }`}
    >
      {/* Mini preview */}
      <div className="mb-3 rounded-lg bg-gray-50 border border-gray-100 p-3 overflow-hidden">
        {preview}
      </div>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-sm font-semibold ${selected ? 'text-brand-700' : 'text-gray-900'}`}>
          {title}
        </span>
      </div>
      <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
      {/* Selection indicator */}
      <div
        className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2 transition-colors flex items-center justify-center ${
          selected ? 'border-brand-500 bg-brand-500' : 'border-gray-300'
        }`}
      >
        {selected && (
          <div className="w-1.5 h-1.5 rounded-full bg-white" />
        )}
      </div>
    </button>
  );
}

/* Mini preview illustrations */
function TablePreview() {
  return (
    <div className="space-y-1.5">
      {/* Header row */}
      <div className="flex gap-1.5">
        <div className="h-2 w-14 rounded-sm bg-gray-300" />
        <div className="h-2 w-10 rounded-sm bg-gray-300" />
        <div className="h-2 w-12 rounded-sm bg-gray-300" />
        <div className="h-2 w-8 rounded-sm bg-gray-300" />
      </div>
      {/* Data rows */}
      {[0.6, 0.8, 0.5, 0.7].map((opacity, i) => (
        <div key={i} className="flex gap-1.5">
          <div className="h-2 rounded-sm bg-gray-200" style={{ width: `${14 * opacity + 4}px`, opacity: 0.5 + opacity * 0.3 }} />
          <div className="h-2 rounded-sm bg-gray-200" style={{ width: `${10 * opacity + 4}px`, opacity: 0.5 + opacity * 0.3 }} />
          <div className="h-2 rounded-sm bg-gray-200" style={{ width: `${12 * opacity + 4}px`, opacity: 0.5 + opacity * 0.3 }} />
          <div className="h-2 rounded-sm bg-gray-200" style={{ width: `${8 * opacity + 4}px`, opacity: 0.5 + opacity * 0.3 }} />
        </div>
      ))}
    </div>
  );
}

function KanbanPreview() {
  return (
    <div className="flex gap-1.5">
      {[3, 2, 1].map((cards, col) => (
        <div key={col} className="flex-1 space-y-1">
          <div className="h-1.5 w-6 rounded-sm bg-gray-300 mb-1.5" />
          {Array.from({ length: cards }).map((_, i) => (
            <div key={i} className="h-4 rounded bg-white border border-gray-200 shadow-[0_0.5px_1px_rgba(0,0,0,0.04)]" />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ─── Step 2: Configure View ─── */

function StepTwo({
  viewType,
  viewName,
  setViewName,
  mode,
  groupByFieldId,
  setGroupByFieldId,
  selectFields,
  columns,
  dragIndex,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  updateColumnLabel,
  removeColumn,
  availableFields,
  filteredAvailableFields,
  filteredSystemFields,
  columnSearch,
  setColumnSearch,
  showColumnPicker,
  setShowColumnPicker,
  toggleFieldColumn,
  toggleSystemColumn,
  filterConfig,
  filters,
  handleFilterChange,
}) {
  return (
    <>
      {/* View Name (editable in edit mode, read-only summary in create) */}
      {mode === 'edit' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">View Name</label>
          <input
            type="text"
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            placeholder="e.g., Active Clients"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400"
          />
        </div>
      )}

      {/* View type badge (read-only in step 2) */}
      {mode === 'create' && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {viewType === 'table' ? (
            <LayoutList className="w-4 h-4" />
          ) : (
            <Kanban className="w-4 h-4" />
          )}
          <span className="font-medium text-gray-700">{viewName}</span>
          <span className="text-gray-400">·</span>
          <span className="capitalize">{viewType} view</span>
        </div>
      )}

      {/* Kanban: Group By */}
      {viewType === 'kanban' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Group By Field</label>
          <p className="text-xs text-gray-400 mb-2">
            Choose a select field to group records into columns.
          </p>
          {selectFields.length === 0 ? (
            <div className="px-3 py-3 text-sm text-amber-700 bg-amber-50 rounded-lg border border-amber-100">
              No select fields found. Add a select field to this object first.
            </div>
          ) : (
            <div className="space-y-1.5">
              {selectFields.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setGroupByFieldId(f.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
                    groupByFieldId === f.id
                      ? 'border-brand-500 bg-brand-50/60 ring-1 ring-brand-500/20'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <List className={`w-4 h-4 flex-shrink-0 ${groupByFieldId === f.id ? 'text-brand-600' : 'text-gray-400'}`} />
                  <span className={`text-sm ${groupByFieldId === f.id ? 'font-medium text-brand-700' : 'text-gray-700'}`}>
                    {f.name}
                  </span>
                  {f.options?.choices && (
                    <span className="text-xs text-gray-400 ml-auto">
                      {f.options.choices.length} options
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Columns */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            {viewType === 'kanban' ? 'Card Fields' : 'Columns'}
          </label>
          <span className="text-xs text-gray-400">
            {columns.length} selected
          </span>
        </div>

        {/* Selected columns — reorderable */}
        {columns.length > 0 && (
          <div className="space-y-1 mb-3">
            {columns.map((col, index) => {
              const field = availableFields.find((f) => f.id === col.field_definition_id);
              const IconComp = col.system_field
                ? Clock
                : (field && FIELD_TYPE_ICONS[field.field_type]) || Type;
              return (
                <div
                  key={`${col.field_definition_id || col.system_field}-${index}`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                    dragIndex === index
                      ? 'border-brand-300 bg-brand-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <GripVertical className="w-4 h-4 text-gray-300 cursor-grab flex-shrink-0" />
                  <IconComp className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    value={col.label}
                    onChange={(e) => updateColumnLabel(index, e.target.value)}
                    className="flex-1 text-sm bg-transparent border-none outline-none text-gray-800 placeholder:text-gray-400"
                    placeholder="Column label"
                  />
                  <span className="text-[11px] text-gray-400 flex-shrink-0">
                    {col.system_field || field?.slug || ''}
                  </span>
                  <button
                    onClick={() => removeColumn(index)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Column picker toggle */}
        <button
          type="button"
          onClick={() => setShowColumnPicker(!showColumnPicker)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-600 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors"
        >
          <span>{showColumnPicker ? 'Hide field picker' : 'Add fields...'}</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showColumnPicker ? 'rotate-180' : ''}`} />
        </button>

        {/* Column picker panel */}
        {showColumnPicker && (
          <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={columnSearch}
                onChange={(e) => setColumnSearch(e.target.value)}
                placeholder="Search fields..."
                className="w-full pl-9 pr-3 py-2.5 text-sm border-b border-gray-200 focus:outline-none placeholder:text-gray-400"
              />
            </div>

            <div className="max-h-48 overflow-y-auto">
              {/* Custom fields */}
              {filteredAvailableFields.length > 0 && (
                <div className="py-1">
                  {filteredAvailableFields.map((field) => {
                    const selected = columns.some((c) => c.field_definition_id === field.id);
                    const IconComp = FIELD_TYPE_ICONS[field.field_type] || Type;
                    return (
                      <button
                        key={field.id}
                        type="button"
                        onClick={() => toggleFieldColumn(field)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                          selected ? 'bg-brand-600 border-brand-600' : 'border-gray-300'
                        }`}>
                          {selected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <IconComp className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className={selected ? 'text-gray-900 font-medium' : 'text-gray-700'}>{field.name}</span>
                        <span className="text-[11px] text-gray-400 ml-auto">{field.field_type}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* System fields */}
              {filteredSystemFields.length > 0 && (
                <div className="border-t border-gray-100 py-1">
                  <p className="px-3 py-1 text-[11px] font-medium text-gray-400 uppercase tracking-wider">System</p>
                  {filteredSystemFields.map((sf) => {
                    const selected = columns.some((c) => c.system_field === sf.system_field);
                    const IconComp = sf.icon;
                    return (
                      <button
                        key={sf.system_field}
                        type="button"
                        onClick={() => toggleSystemColumn(sf)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                          selected ? 'bg-brand-600 border-brand-600' : 'border-gray-300'
                        }`}>
                          {selected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <IconComp className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className={selected ? 'text-gray-900 font-medium' : 'text-gray-700'}>{sf.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {filteredAvailableFields.length === 0 && filteredSystemFields.length === 0 && (
                <p className="px-3 py-4 text-sm text-gray-400 text-center">No fields match your search.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      {filterConfig.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Filters</label>
          <div className="space-y-3">
            {filterConfig.map((field) => (
              <div key={field.key}>
                <label className="block text-xs text-gray-500 mb-1">{field.label}</label>
                {field.type === 'select' && (
                  <select
                    value={filters[field.key] || ''}
                    onChange={(e) => handleFilterChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                  >
                    <option value="">All</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
                {field.type === 'text' && (
                  <input
                    type="text"
                    value={filters[field.key] || ''}
                    onChange={(e) => handleFilterChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400"
                  />
                )}
                {field.type === 'boolean' && (
                  <select
                    value={filters[field.key] ?? ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? undefined : e.target.value === 'true';
                      handleFilterChange(field.key, val);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                  >
                    <option value="">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default ViewEditorModal;
